import { Box, Flex, HStack, NumberInput, NumberInputField, SimpleGrid, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { RiSmartphoneLine } from 'react-icons/ri';

import { SectionCard, StatusBadge } from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';
// Campaign-owned inputs preserve calendar dates and recipient-local clock values.
import { DateField } from '../_ui/DateField';
import { TimeField } from '../_ui/TimeField';
import { useListSendersQuery } from 'api/metaWhatsappApi';
import { ChoiceCard, ChipToggle } from '../_ui/controls';

/**
 * From which numbers, and when.
 *
 * ★ THE WINDOW IS IN THE RECIPIENT'S TIME ZONE, NOT YOURS. "10:00 to 12:00"
 * means ten in the morning where each person is — a Pakistani and an Emirati
 * recipient are messaged an hour apart in real time. This is the single thing
 * on this screen people most often assume works the other way, so the screen
 * says it out loud rather than hoping.
 *
 * ★ PINNING IS A RESTRICTION, NOT AN ALLOCATION. Leaving every number unpinned
 * lets the engine choose one at send time from whatever is healthy, which is
 * what makes pausing a number harmless. Pinning narrows that pool; it never
 * assigns particular people to particular numbers.
 */

const WEEKDAYS = [
	{ value: 0, label: 'Sun' },
	{ value: 1, label: 'Mon' },
	{ value: 2, label: 'Tue' },
	{ value: 3, label: 'Wed' },
	{ value: 4, label: 'Thu' },
	{ value: 5, label: 'Fri' },
	{ value: 6, label: 'Sat' },
];

const num = (v) => Number(v ?? 0).toLocaleString();

export default function StepSchedule({ draft, patch }) {
	const ui = useCampaignUI();
	const { data: senders, isLoading } = useListSendersQuery();

	const schedule = draft.schedule ?? {};
	const pinned = draft.senders?.pinnedPhoneNumberIds ?? [];
	const list = Array.isArray(senders) ? senders : [];

	const setSchedule = (key, value) => patch({ schedule: { ...schedule, [key]: value } });

	const togglePin = (phoneNumberId) => {
		const next = pinned.includes(phoneNumberId)
			? pinned.filter((id) => id !== phoneNumberId)
			: [...pinned, phoneNumberId];
		patch({ senders: { ...(draft.senders ?? {}), pinnedPhoneNumberIds: next } });
	};

	const toggleWeekday = (value) => {
		const excluded = schedule.excludedWeekdays ?? [];
		const next = excluded.includes(value) ? excluded.filter((d) => d !== value) : [...excluded, value];
		setSchedule('excludedWeekdays', next);
	};

	// What the chosen pool can carry in a day — the number that decides how long
	// this campaign takes.
	const usable = pinned.length ? list.filter((s) => pinned.includes(s.phoneNumberId)) : list;
	const dailyCapacity = usable.reduce((sum, s) => sum + (s.capacity?.blocked ? 0 : s.capacity?.cap ?? 0), 0);

	return (
		<Box>
			<SectionCard title='When' subtitle='Dates are calendar dates; the hours are local to each recipient.' mb={5}>
				<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
					<Box>
						<Text fontSize='xs' color={ui.textMuted} mb={1}>
							First day
						</Text>
						<DateField
							ariaLabel='First day'
							placeholder='Choose the first day'
							value={schedule.startDate ?? ''}
							onChange={(v) => setSchedule('startDate', v)}
							maxDate={schedule.endDate || undefined}
						/>
					</Box>
					<Box>
						<Text fontSize='xs' color={ui.textMuted} mb={1}>
							Last day
						</Text>
						<DateField
							ariaLabel='Last day'
							placeholder='Choose the last day'
							value={schedule.endDate ?? ''}
							onChange={(v) => setSchedule('endDate', v)}
							minDate={schedule.startDate || undefined}
						/>
					</Box>
				</SimpleGrid>

				<Text fontSize='xs' color={ui.textMuted} mb={1}>
					Hours — in each recipient's own time zone
				</Text>
				<HStack spacing={2} mb={2}>
					<TimeField
						ariaLabel='Send from'
						maxW='130px'
						value={schedule.windowStart ?? '09:00'}
						onChange={(v) => setSchedule('windowStart', v)}
					/>
					<Text fontSize='sm' color={ui.textMuted}>
						to
					</Text>
					<TimeField
						ariaLabel='Send until'
						maxW='130px'
						value={schedule.windowEnd ?? '20:00'}
						onChange={(v) => setSchedule('windowEnd', v)}
					/>
				</HStack>
				<Text fontSize='xs' color={ui.textMuted} mb={4}>
					Someone in Karachi and someone in Dubai both receive it at ten in the morning — an hour apart in
					real time. Nobody is woken at three.
				</Text>

				<Text fontSize='xs' color={ui.textMuted} mb={2}>
					Days to skip
				</Text>
				{/* Tap a day to skip it. A struck-through pill reads as "not this
				    one" at a glance, where a ticked box beside "Sun" read as the
				    opposite to half the people who tried it. */}
				<Flex gap={2} wrap='wrap' role='group' aria-label='Days to skip'>
					{WEEKDAYS.map((day) => (
						<ChipToggle
							key={day.value}
							tone='skip'
							checked={(schedule.excludedWeekdays ?? []).includes(day.value)}
							onToggle={() => toggleWeekday(day.value)}
							aria-label={`Skip ${day.label}`}
						>
							{day.label}
						</ChipToggle>
					))}
				</Flex>
			</SectionCard>

			<SectionCard
				title='Which numbers'
				subtitle='Leave them all unpinned and the engine picks a healthy one for each message. Pin some to restrict it.'
			>
				{isLoading ? (
					<Flex justify='center' py={8}>
						<Spinner color={ui.accentInk} />
					</Flex>
				) : !list.length ? (
					<Text fontSize='sm' color={ui.textMuted}>
						No numbers are connected yet.
					</Text>
				) : (
					<>
						<SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3} mb={4}>
							{list.map((sender) => {
								const isPinned = pinned.includes(sender.phoneNumberId);
								const blocked = sender.capacity?.blocked;
								return (
									// The whole card pins the number. It already drew a ring when
									// pinned; only a 16px box beside it took the click, which is
									// the single most common thing people got wrong on this step.
									<ChoiceCard
										key={sender.phoneNumberId}
										multi
										selected={isPinned}
										onSelect={() => togglePin(sender.phoneNumberId)}
										icon={RiSmartphoneLine}
										title={sender.displayPhoneNumber || sender.phoneNumberId}
										description={blocked ? 'Not sending right now' : `${num(sender.capacity?.remaining)} left today`}
										aria-label={`Pin ${sender.displayPhoneNumber || sender.phoneNumberId}`}
										opacity={blocked ? 0.6 : 1}
									/>
								);
							})}
						</SimpleGrid>

						<Flex justify='space-between' align='center' gap={4} wrap='wrap'>
							<Tooltip
								hasArrow
								label='What the chosen numbers can carry between them in one day. It decides how long this campaign takes.'
							>
								<Text fontSize='sm' color={ui.textSecondary}>
									Daily capacity: <strong>{num(dailyCapacity)}</strong> messages
								</Text>
							</Tooltip>

							<HStack spacing={2}>
								<Text fontSize='xs' color={ui.textMuted}>
									Cap for this campaign
								</Text>
								<NumberInput
									size='sm'
									maxW='90px'
									min={1}
									max={100}
									value={draft.senders?.capPercent ?? ''}
									onChange={(v) =>
										patch({
											senders: {
												...(draft.senders ?? {}),
												capPercent: v === '' ? undefined : Number(v),
											},
										})
									}
								>
									<NumberInputField
										bg={ui.inputBg}
										borderColor={ui.border}
										color={ui.textPrimary}
										placeholder='default'
									/>
								</NumberInput>
								<Text fontSize='xs' color={ui.textMuted}>
									%
								</Text>
							</HStack>
						</Flex>
					</>
				)}
			</SectionCard>
		</Box>
	);
}
